import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { DocumentThemeSync } from './DocumentThemeSync';
import { AuthProvider, useAuth } from './auth/context/AuthContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import DefaultLayout from './layout/DefaultLayout';

// Import authentication pages
import LoginPage from './auth/LoginPage';
import { SignupPage } from './auth/SignupPage';
import { RequestPasswordResetPage } from './auth/RequestPasswordResetPage';
import { PasswordResetPage } from './auth/PasswordResetPage';

// Import landing page
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import PurchasePage from './pages/PurchasePage';
import CookieConsentBanner from './components/cookie-consent/Banner';
import UpcomingPage from './pages/UpcomingPage';
import FaqPage from './pages/FaqPage';
import UseCasesPage from './pages/UseCasesPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import CookiesPage from './pages/CookiesPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPage from './pages/PrivacyPage';
import CareersPage from './pages/CareersPage';
import CareerJobPage from './pages/CareerJobPage';
import SupportPage from './pages/SupportPage';
import MarketingAssetsPage from './pages/MarketingAssetsPage';

// Import actual pages
import { UploadPricingTablePage } from './pages/UploadPricingTablePage';
import { PricingTablesPage } from './pages/PricingTablesPage';
import { ViewPricingTablePage } from './pages/ViewPricingTablePage';
import { PricingTableVariationsPage } from './pages/PricingTableVariationsPage';
import { InstallPage } from './pages/InstallPage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import SubscriptionPage from './pages/SubscriptionPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import EmailVerificationPage from './pages/EmailVerificationPage';

// Import new AI optimization pages
import { FindingsPage } from './pages/FindingsPage';
import { OptimizationDashboardPage } from './pages/OptimizationDashboardPage';
import { OptimizationResultsPage } from './pages/OptimizationResultsPage';
import { MonitoringDashboardPage } from './pages/MonitoringDashboardPage';
import { SettingsPage } from './pages/SettingsPage';

// Placeholder components - these will be implemented in future slices
const DashboardPage = () => <div className="p-8">Dashboard Page - Coming Soon</div>;
const MessagesPage = () => <div className="p-8">Messages Page - Coming Soon</div>;
const AccountPage = () => <div className="p-8">Account Page - Coming Soon</div>;
const AdminPage = () => <div className="p-8">Admin Page - Coming Soon</div>;

// Wrapper component for OptimizationResultsPage to get jobId from URL params
const OptimizationResultsWrapper = () => {
  const { jobId } = useParams();
  return <OptimizationResultsPage jobId={jobId || ''} />;
};

const AppRoutes: React.FC = () => {
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/purchase" element={<PurchasePage />} />
      <Route path="/upcoming" element={<UpcomingPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/use-cases" element={<UseCasesPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/careers/jobs/:jobId" element={<CareerJobPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/marketing-assets" element={<MarketingAssetsPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<EmailVerificationPage />} />
      <Route path="/request-password-reset" element={<RequestPasswordResetPage />} />
      <Route path="/reset-password" element={<PasswordResetPage />} />
      
      {/* Protected routes with layout */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <DashboardPage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <AnalyticsDashboardPage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      <Route path="/upload-pricing-table" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <UploadPricingTablePage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      <Route path="/pricing-tables" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <PricingTablesPage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      <Route path="/pricing-tables/:pricingTableId" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <ViewPricingTablePage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      <Route path="/pricing-tables/:id/variations" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <PricingTableVariationsPage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      <Route path="/install" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <InstallPage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      
      {/* AI Pricing Findings Page */}
      <Route path="/findings" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <FindingsPage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      
      {/* Optimization Dashboard Page */}
      <Route path="/optimization/dashboard" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <OptimizationDashboardPage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      
      {/* Optimization Results Page */}
      <Route path="/optimization/results/:jobId" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <OptimizationResultsWrapper />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      
      {/* System Monitoring Dashboard Page */}
      <Route path="/monitoring/dashboard" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <MonitoringDashboardPage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <MessagesPage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <SettingsPage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <DefaultLayout user={user!} onLogout={handleLogout}>
            <AdminPage />
          </DefaultLayout>
        </ProtectedRoute>
      } />
              <Route path="/subscription" element={
          <ProtectedRoute>
            <DefaultLayout user={user!} onLogout={handleLogout}>
              <SubscriptionPage />
            </DefaultLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin>
            <DefaultLayout user={user!} onLogout={handleLogout}>
              <AdminDashboardPage />
            </DefaultLayout>
          </ProtectedRoute>
        } />
      
      {/* Redirect to dashboard for unknown routes */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <DocumentThemeSync />
      <AuthProvider>
        <div className="App">
          <AppRoutes />
          <CookieConsentBanner />
        </div>
      </AuthProvider>
    </Router>
  );
};

export default App;
